import { useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Check, Minus, Scales } from "@phosphor-icons/react";
import { fmtBytes } from "@/lib/format";
import { track } from "@/lib/analytics";

const COMPARE_PLANS = ["free", "basic", "family", "business"];

// Rows grouped by section. `type`: cap = capability ✓/—; value = Limited/number.
const SECTIONS = [
  { title: "Storage", rows: [{ label: "Secure storage", type: "storage" }] },
  { title: "Scanning & OCR", rows: [
    { label: "DuruScan", type: "cap", cap: "duruscan" },
    { label: "Scans / month", type: "quota", field: "scans_per_month" },
    { label: "OCR / month", type: "quota", field: "ocr_per_month" },
  ]},
  { title: "Secure sharing", rows: [
    { label: "Password-protected links", type: "cap", cap: "password_links" },
    { label: "Advanced sharing controls", type: "cap", cap: "advanced_sharing" },
  ]},
  { title: "DuruSign", rows: [{ label: "E-signatures", type: "cap", cap: "durusign" }] },
  { title: "DuruFlow", rows: [{ label: "Workflows & automation", type: "soon" }] },
  { title: "DuruTwin", rows: [{ label: "Living document intelligence", type: "cap", cap: "durutwin" }] },
  { title: "DuruVerify", rows: [
    { label: "DuruVerify", type: "cap", cap: "duruverify" },
    { label: "ID Check", type: "cap", cap: "id_check" },
  ]},
  { title: "Expiration monitoring", rows: [{ label: "Expiration reminders", type: "cap", cap: "expiration_reminders" }] },
  { title: "Business capabilities", rows: [
    { label: "DuruRooms secure spaces", type: "cap", cap: "dururooms" },
    { label: "Audit logs", type: "cap", cap: "audit_logs" },
    { label: "Compliance controls", type: "cap", cap: "compliance" },
  ]},
];

function Cell({ row, plan, dark }) {
  if (!plan) return <span className="text-slate-400">—</span>;
  if (row.type === "storage") return <span className="font-medium">{fmtBytes(plan.storage_bytes)}</span>;
  if (row.type === "soon") return <span className={dark ? "text-white/40" : "text-slate-400"}>Soon</span>;
  if (row.type === "quota") {
    const v = plan[row.field];
    if (!plan.capabilities?.duruscan) return <Minus size={15} className={dark ? "text-white/25" : "text-slate-300"} aria-label="Not included" />;
    return <span className="text-xs font-medium">{v == null ? "Unlimited" : Number(v).toLocaleString()}</span>;
  }
  // capability
  const on = !!plan.capabilities?.[row.cap];
  const early = (plan.early_access || []).includes(row.cap);
  return on
    ? <span className="inline-flex items-center gap-1 text-primary"><Check size={16} weight="bold" aria-label="Included" />{early && <span className="text-[9px] uppercase tracking-wide">EA</span>}</span>
    : <Minus size={15} className={dark ? "text-white/25" : "text-slate-300"} aria-label="Not included" />;
}

export default function CompareFeatures({ plans = [], theme = "light" }) {
  const dark = theme === "dark";
  const [open, setOpen] = useState(false);
  const cols = COMPARE_PLANS.map((id) => plans.find((p) => p.plan_id === id)).filter(Boolean);

  const toggle = () => { const n = !open; setOpen(n); if (n) track("compare_plans_opened"); };

  const head = dark ? "text-white/60" : "text-slate-500";
  const rowBorder = dark ? "border-white/8" : "border-slate-100";
  const btnCls = dark ? "bg-transparent border-white/20 text-white hover:bg-white/10" : "";

  return (
    <div className="mt-10" data-testid="compare-features">
      <div className="text-center">
        <Button onClick={toggle} variant="outline" className={`rounded-full ${btnCls}`} data-testid="compare-toggle-btn">
          <Scales size={16} className="mr-1.5" /> {open ? "Hide comparison" : "Compare all features"}
        </Button>
      </div>

      {open && (
        <div className="mt-6 max-w-4xl mx-auto" data-testid="compare-panel">
          {/* Sticky plan header */}
          <div className={`grid grid-cols-[1.4fr_repeat(4,1fr)] gap-1 items-end pb-2 mb-1 border-b ${rowBorder} text-center`}>
            <span />
            {cols.map((p) => (
              <span key={p.plan_id} className={`text-xs sm:text-sm font-semibold ${p.plan_id === "personal" ? "text-primary" : ""}`}>{p.name}</span>
            ))}
          </div>
          <Accordion type="multiple" defaultValue={SECTIONS.map((s) => s.title)} className="w-full">
            {SECTIONS.map((s) => (
              <AccordionItem key={s.title} value={s.title} className={`border-b ${rowBorder}`}>
                <AccordionTrigger className="py-3 text-sm font-semibold" data-testid={`compare-section-${s.title.replace(/\s+/g, "-").toLowerCase()}`}>{s.title}</AccordionTrigger>
                <AccordionContent>
                  <div className="pb-2">
                    {s.rows.map((row) => (
                      <div key={row.label} className={`grid grid-cols-[1.4fr_repeat(4,1fr)] gap-1 items-center py-2 border-t ${rowBorder} text-center`}>
                        <span className={`text-left text-xs sm:text-sm ${head}`}>{row.label}</span>
                        {cols.map((p) => (
                          <span key={p.plan_id} className="flex justify-center"><Cell row={row} plan={p} dark={dark} /></span>
                        ))}
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <p className={`text-center text-xs mt-3 ${head}`}>EA = early access. Numbers are monthly allowances.</p>
        </div>
      )}
    </div>
  );
}
